import { useEffect, useId, useRef, useState } from "react";
import { Accordion, AccordionDetails, AccordionSummary, Alert, DialogActions, DialogContent, DialogTitle, LinearProgress, useMediaQuery, useTheme } from "@mui/material";
import DeleteOutlineRounded from "@mui/icons-material/DeleteOutlineRounded";
import ExpandMoreRounded from "@mui/icons-material/ExpandMoreRounded";
import { useTranslation } from "react-i18next";
import { MonitorButton, MonitorDialog, MonitorInput } from "./MonitorControls";
import { api } from "../utils/helpers";

const ENDPOINT = "/api/cicd/transport/promotions/package-retirement";
const validWaitingJobs = value => value === undefined || (Array.isArray(value)
  && value.every(job => job && typeof job.id === "string" && job.id.trim()
    && ["qas", "preprod", "prod"].includes(job.targetStage))
  && new Set(value.map(job => job.id)).size === value.length);
const validGeneration = value => Number.isSafeInteger(value) && value > 0;
const validPending = (value, spaceId, packageId) => value && value.spaceId === spaceId && value.packageId === packageId
  && value.confirmation === packageId && ["revision", "previewToken", "requestId"].every(key => typeof value[key] === "string" && value[key]);

export function PackageRetirementButton({ item, disabled, onClick }) {
  const { t } = useTranslation("freigabe");
  if (!item) return null;
  return <MonitorButton className="promotion-package-retirement-action" startIcon={<DeleteOutlineRounded/>}
    disabled={disabled || (item.pending && !item.pendingRetirement)} aria-haspopup="dialog"
    aria-label={t(item.pendingRetirement ? "packageRetirement.retryNamed" : "packageRetirement.removeNamed", { id: item.packageId })}
    onClick={() => onClick(item)}>{t(item.pendingRetirement ? "packageRetirement.retry" : "packageRetirement.remove")}</MonitorButton>;
}

export default function PromotionPackageRetirement({ item, current, spaceId, canonicalSpaceId = spaceId, disabled, canResume = false, onStart, onEnd, onClose }) {
  const { t } = useTranslation("freigabe");
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down("sm"));
  const id = useId();
  const pending = validPending(item.pendingRetirement, canonicalSpaceId, item.packageId) ? item.pendingRetirement : null;
  const [preview, setPreview] = useState(null);
  const [confirmation, setConfirmation] = useState(pending ? item.packageId : "");
  const [loading, setLoading] = useState(!pending);
  const [busy, setBusy] = useState(false);
  const [uncertain, setUncertain] = useState(!!pending);
  const [error, setError] = useState(null);
  const payload = useRef(pending ? { ...pending } : null);
  const writing = useRef(false);
  const alive = useRef(true);
  const readVersion = useRef(0);
  const latest = useRef({ current, disabled, spaceId });
  latest.current = { current, disabled, spaceId };
  const errorText = failure => {
    const code = failure?.code || failure?.body?.code;
    for (const prefix of ["packageRetirement.errors", "packageTopic.errors", "topics.errors", "preflight.errors", "promotion.blocked"]) {
      const message = t(`${prefix}.${code}`, { defaultValue: "" });
      if (message) return message;
    }
    return t("packageRetirement.unavailable");
  };
  const loadPreview = async () => {
    const state = latest.current;
    if (writing.current || payload.current || state.disabled || !state.current) { setLoading(false); return; }
    const version = ++readVersion.current;
    const revision = state.current.revision;
    setLoading(true); setPreview(null); setConfirmation(""); setError(null);
    try {
      const result = await api(`${ENDPOINT}/preview`, { method: "POST", body: JSON.stringify({ spaceId, packageId: item.packageId, revision }) });
      if (!alive.current || version !== readVersion.current) return;
      const value = result?.preview;
      if (!value || value.packageId !== item.packageId || value.revision !== revision || value.canRemove !== true
        || typeof value.previewToken !== "string" || !value.previewToken || !validGeneration(value.generation)
        || !Number.isSafeInteger(value.artifactCount) || value.artifactCount < 0 || !Array.isArray(value.paths)
        || !value.paths.every(path => typeof path === "string" && path.length > 0)
        || !validWaitingJobs(value.waitingJobs)) throw new Error("INVALID_PREVIEW");
      setPreview(value);
    } catch (failure) {
      if (alive.current && version === readVersion.current) setError(errorText(failure));
    } finally {
      if (alive.current && version === readVersion.current) setLoading(false);
    }
  };
  useEffect(() => {
    alive.current = true;
    loadPreview();
    return () => { alive.current = false; readVersion.current += 1; };
    // A preview is requested once on opening, or explicitly refreshed by the administrator.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const changed = !current || (preview && current.revision !== preview.revision);
  const pendingElsewhere = current?.pending && (!current.pendingRetirement
    || current.pendingRetirement.requestId !== payload.current?.requestId);
  const resumeAllowed = uncertain && canResume && validPending(current?.pendingRetirement, canonicalSpaceId, item.packageId)
    && current.pendingRetirement.requestId === payload.current?.requestId;
  const valid = (!disabled || resumeAllowed) && !busy && !loading && !pendingElsewhere
    && (uncertain ? !!payload.current && validGeneration(preview?.generation ?? item.pendingRetirementGeneration)
      : !!preview && !changed && confirmation === item.packageId);
  const close = () => { if (!writing.current) onClose(); };
  const submit = async () => {
    if (!valid || writing.current) return;
    if (!payload.current) payload.current = { spaceId, packageId: item.packageId, revision: preview.revision,
      previewToken: preview.previewToken, confirmation, requestId: window.crypto.randomUUID() };
    readVersion.current += 1;
    writing.current = true; setBusy(true); setError(null); onStart();
    let done = false;
    try {
      const result = await api(ENDPOINT, { method: "POST", body: JSON.stringify(payload.current) });
      const receipt = result?.retirement;
      if (receipt?.status !== "completed" || receipt.removed !== true || receipt.packageId !== item.packageId
        || receipt.requestId !== payload.current.requestId || !validGeneration(receipt.generation)
        || receipt.generation !== (preview?.generation ?? item.pendingRetirementGeneration)) throw new Error("INVALID_RECEIPT");
      done = true;
      if (alive.current) setUncertain(false);
    } catch (failure) {
      if (alive.current) {
        setError(errorText(failure));
        const rejected = failure.status >= 400 && failure.status < 500 && ![408, 429].includes(failure.status)
          && failure.code !== "PACKAGE_RETIREMENT_PENDING" && !uncertain;
        setUncertain(!rejected);
        if (rejected) { payload.current = null; setPreview(null); setConfirmation(""); }
      }
    } finally {
      try { await onEnd(); } finally {
        writing.current = false;
        if (alive.current) { setBusy(false); if (done) onClose(true); }
      }
    }
  };
  return <MonitorDialog className="promotion-dialog promotion-package-retirement-dialog" maxWidth="sm" fullScreen={mobile}
    onClose={close} aria-labelledby={`${id}-title`} aria-describedby={`${id}-effects`}>
    <DialogTitle id={`${id}-title`}>{t("packageRetirement.title")}</DialogTitle>
    <DialogContent>
      {item.packageName && item.packageName !== item.packageId && <p><strong>{item.packageName}</strong></p>}
      <p className="promotion-package-id"><strong>{t("packageRetirement.packageId")}</strong><br/>{item.packageId}</p>
      <div id={`${id}-effects`}>
        <p>{t("packageRetirement.effect")}</p>
        <p>{t("packageRetirement.preserved")}</p>
        <p>{t("packageRetirement.nextTransport")}</p>
      </div>
      {loading && <LinearProgress aria-label={t("packageRetirement.loading")}/>}
      {preview && <>
        <p>{t("packageRetirement.scope", { count: preview.artifactCount, files: preview.paths.length })}</p>
        {preview.waitingJobs?.length > 0 && <Alert severity="warning">
          <p>{t("packageRetirement.waitingJobs", { count: preview.waitingJobs.length })}</p>
          <ul>{preview.waitingJobs.map(job => <li key={job.id}>
            {t("packageRetirement.waitingJob", { id: job.id, stage: job.targetStage === "preprod" ? "PRE-PROD" : job.targetStage.toUpperCase() })}
          </li>)}</ul>
        </Alert>}
        <Accordion className="promotion-accordion">
          <AccordionSummary expandIcon={<ExpandMoreRounded/>}>{t("packageRetirement.paths", { count: preview.paths.length })}</AccordionSummary>
          <AccordionDetails><ul>{preview.paths.map(path => <li key={path}>{path}</li>)}</ul></AccordionDetails>
        </Accordion>
      </>}
      {uncertain ? <Alert severity="warning">{t("packageRetirement.uncertain")}</Alert> : <>
        <p><label htmlFor={`${id}-confirmation`}>{t("packageRetirement.confirmation")}</label></p>
        <MonitorInput id={`${id}-confirmation`} fullWidth aria-label={t("packageRetirement.confirmation")}
          value={confirmation} disabled={busy || loading || disabled || !preview || changed}
          onChange={event => setConfirmation(event.target.value)} autoComplete="off"/>
      </>}
      {changed && !uncertain && <Alert severity="warning">{t("packageRetirement.changed")}</Alert>}
      {pendingElsewhere && <Alert severity="warning">{t("packageTopic.pendingElsewhere")}</Alert>}
      {disabled && !busy && !resumeAllowed && <Alert severity="warning">{t("packageTopic.unavailable")}</Alert>}
      {error && <Alert severity="error">{error}</Alert>}
      {!loading && !uncertain && (!preview || changed) && <MonitorButton disabled={busy || disabled || !current || pendingElsewhere}
        onClick={loadPreview}>{t("packageRetirement.refresh")}</MonitorButton>}
    </DialogContent>
    <DialogActions>
      <MonitorButton autoFocus disabled={busy} onClick={close}>{t("topics.cancel")}</MonitorButton>
      <MonitorButton className="iio-btn--signal" disabled={!valid} onClick={submit}>
        {t(busy ? "promotion.sending" : uncertain ? "packageRetirement.retry" : "packageRetirement.confirm")}
      </MonitorButton>
    </DialogActions>
  </MonitorDialog>;
}
